import { useMemo } from 'react';
import { Button, Input } from '@artstorefronts/ui';
import { PlusCircle, Search } from 'lucide-react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import lunr from 'lunr';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import AdminLayout from '../../../components/AdminLayout';
import { SparkTable } from './SparkTable';

import { COMPLETION_TYPES_QUERY } from '../../../graphql/queries';

function AdminSparks() {
  const { t } = useTranslation('admin');
  const { data } = useQuery(COMPLETION_TYPES_QUERY);
  const [search, setSearch] = useState('');
  const [searchIndex, setSearchIndex] = useState<lunr.Index | null>(null);

  // Create search index when data loads
  useEffect(() => {
    if (data?.completionTypes) {
      const idx = lunr(function () {
        this.field('name');
        this.field('description');
        this.field('id');

        data.completionTypes
          .filter((spark: any) => spark.id)
          .forEach((spark: any) => {
            this.add({
              id: spark.id,
              name: spark.name || '',
              description: spark.description || '',
            });
          });
      });
      setSearchIndex(idx);
    }
  }, [data]);

  // Filter sparks based on search
  const filteredSparks = useMemo(() => {
    if (!data?.completionTypes) return [];
    if (!search.trim() || !searchIndex) return data.completionTypes;

    try {
      // Add wildcards to make the search more forgiving
      const searchTerm = search
        .trim()
        .split(' ')
        .map((term) => `${term}*`)
        .join(' ');

      const results = searchIndex.search(searchTerm);

      return results
        .map((result) => data.completionTypes.find((spark: any) => spark.id === result.ref))
        .filter(Boolean);
    } catch (e) {
      console.error(e);
      return data.completionTypes;
    }
  }, [data?.completionTypes, search, searchIndex]);

  // Update the search input handler
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleDelete = (/*id: any*/) => {};

  return (
    <AdminLayout
      title={t('sparks.page-title')}
      description={t('sparks.page-description')}
      action={
        <Button asChild>
          <Link to="/admin/sparks/add">
            <PlusCircle className="size-4" />
            {t('sparks.add-spark')}
          </Link>
        </Button>
      }
    >
      <>
        <div className="flex items-center justify-between mb-4">
          <div className="relative w-[300px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('sparks.search-placeholder')}
              className="pl-8"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <div className="text-sm text-muted-foreground">
            {t('sparks.record-count', { count: filteredSparks.length })}
          </div>
        </div>

        <SparkTable sparks={filteredSparks} onDelete={handleDelete} />
      </>
    </AdminLayout>
  );
}

export default AdminSparks;
